
import React, { useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Save, Trash } from "lucide-react";

type IncompleteProposal = {
  id: string;
  title: string;
  status: string;
  lastOpened: string;
  proposalSent: boolean;
};

const IncompleteProposals = () => {
  // Render the title in the outlet context
  if (useOutletContext() === "title-only") {
    return "Incomplete Proposals";
  }
  
  const [incompleteProposals, setIncompleteProposals] = useState<IncompleteProposal[]>([]);
  const { toast } = useToast();
  
  // Load incomplete proposals from localStorage or initialize with sample data
  useEffect(() => {
    const loadProposals = () => {
      // Sample data for demonstration
      const sampleData = [
        {
          id: "inc-1",
          title: "Corporate Litigation Review",
          status: "Draft",
          lastOpened: "2025-04-22",
          proposalSent: false
        },
        {
          id: "inc-2",
          title: "IP Protection Strategy",
          status: "In Progress",
          lastOpened: "2025-04-18",
          proposalSent: true
        },
        {
          id: "inc-3",
          title: "Employment Law Compliance",
          status: "Pending Review",
          lastOpened: "2025-04-10",
          proposalSent: false
        }
      ];
      
      // Try to load from localStorage first
      const savedProposals = localStorage.getItem("incomplete-proposals");
      if (savedProposals) {
        try {
          const parsedProposals = JSON.parse(savedProposals);
          setIncompleteProposals(parsedProposals);
        } catch (error) {
          console.error("Error parsing incomplete proposals:", error);
          toast({
            title: "Error loading proposals",
            description: "There was a problem loading your incomplete proposals. Loading sample data instead.",
            variant: "destructive"
          });
          // Set sample data if parsing fails
          setIncompleteProposals(sampleData);
          localStorage.setItem("incomplete-proposals", JSON.stringify(sampleData));
        }
      } else {
        // If no data in localStorage, set sample data
        setIncompleteProposals(sampleData);
        localStorage.setItem("incomplete-proposals", JSON.stringify(sampleData));
      }
    };
    
    loadProposals();
    
    // Add event listener for storage changes to refresh data
    window.addEventListener("storage", (e) => {
      if (e.key === "incomplete-proposals") {
        loadProposals();
      }
    });
    
    return () => {
      window.removeEventListener("storage", () => {});
    };
  }, [toast]);

  // Format date
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(date);
    } catch (error) {
      return "Invalid date";
    }
  };

  // Handle save as template
  const handleSaveAsTemplate = (id: string, title: string) => {
    // In a real application, you would save this proposal as a template
    toast({
      title: "Template Saved",
      description: `"${title}" has been saved as a template.`
    });
  };

  // Handle delete proposal
  const handleDeleteProposal = (id: string, title: string) => {
    // Remove the proposal from the list
    const updatedProposals = incompleteProposals.filter(proposal => proposal.id !== id);
    setIncompleteProposals(updatedProposals);
    
    // Update localStorage
    localStorage.setItem("incomplete-proposals", JSON.stringify(updatedProposals));
    
    toast({
      title: "Proposal Deleted",
      description: `"${title}" has been deleted.`
    });
  };
  
  return (
    <div className="animate-fade-in">
      <div className="bg-white shadow rounded-lg p-6 border border-gray-200">
        <h2 className="text-2xl font-semibold mb-4">Incomplete Proposals</h2>
        <p className="text-gray-600 mb-6">
          View and manage your incomplete proposals here.
        </p>
        
        {incompleteProposals.length === 0 ? (
          <div className="p-8 border border-dashed border-gray-300 rounded-lg text-center">
            <p className="text-gray-500">Your incomplete proposals will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name of Matter</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Opened</TableHead>
                    <TableHead>Proposal Sent</TableHead>
                    <TableHead>Save as Template</TableHead>
                    <TableHead>Delete</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {incompleteProposals.map((proposal) => (
                    <TableRow key={proposal.id}>
                      <TableCell className="font-medium">
                        <Link to={`/proposal/${proposal.id}`} className="hover:underline text-blue-600">
                          {proposal.title || "Untitled Proposal"}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100">
                          {proposal.status}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(proposal.lastOpened)}</TableCell>
                      <TableCell>
                        {proposal.proposalSent ? "Yes" : "No"}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleSaveAsTemplate(proposal.id, proposal.title)}
                        >
                          <Save className="mr-1 h-4 w-4" />
                          Save as Template
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleDeleteProposal(proposal.id, proposal.title)}
                        >
                          <Trash className="mr-1 h-4 w-4" />
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncompleteProposals;
